import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { dismissNotification } from '../features/notificationSlice';
import { setChannel, setBrowserUrl, setAppChannelUrl } from '../features/channelSlice';

// iOS-style notification bubble with channel icon (moved inline from Notification.js)
const Notification = ({ iconInfo, title, message, isTablet = false }) => {
  if (!title && !message) {
    return null;
  }

  const renderIcon = () => {
    if (iconInfo?.type === 'custom') {
      return (
        <img 
          src={iconInfo.url} 
          alt="channel icon" 
          className="w-8 h-8 object-cover rounded-xl"
          style={{ 
            filter: 'none', // Remove invert filter for natural appearance
            backgroundColor: '#f0f0f0' // Light background in case of transparent icons
          }}
          onError={(e) => {
            // Fallback to material icon if custom icon fails
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    } else if (iconInfo?.type === 'material') {
      return (
        <div 
          className="w-8 h-8 rounded-xl flex items-center justify-center shadow-sm"
          style={{ 
            backgroundColor: iconInfo.color,
            background: `linear-gradient(135deg, ${iconInfo.color} 0%, ${iconInfo.color}dd 100%)`
          }}
        >
          <span 
            className="material-icons text-white"
            style={{ fontSize: '18px', fontWeight: '500' }}
          >
            {iconInfo.icon}
          </span>
        </div>
      );
    }
    
    // Default fallback - use app icon style
    return (
      <div 
        className="w-8 h-8 rounded-xl flex items-center justify-center shadow-sm"
        style={{ 
          backgroundColor: '#007AFF',
          background: 'linear-gradient(135deg, #007AFF 0%, #007AFFdd 100%)'
        }}
      >
        <span 
          className="material-icons text-white"
          style={{ fontSize: '18px', fontWeight: '500' }}
        >
          apps
        </span>
      </div>
    );
  };

  return (
    <div 
      className={`absolute top-2 left-1/2 -translate-x-1/2 w-[92%] ${isTablet ? 'max-w-xl' : 'max-w-sm'} bg-gray-900 bg-opacity-85 backdrop-blur-xl rounded-2xl shadow-2xl p-4 flex items-start space-x-3 z-50`}
      style={{ 
        animationFillMode: 'forwards', 
        boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)',
        border: '0.5px solid rgba(255,255,255,0.1)'
      }}
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
        {renderIcon()}
        {/* Hidden fallback for custom icon failures */}
        {iconInfo?.type === 'custom' && (
          <div 
            className="w-8 h-8 rounded-xl items-center justify-center"
            style={{ 
              backgroundColor: iconInfo?.fallbackColor || '#007AFF',
              display: 'none'
            }}
          >
            <span 
              className="material-icons text-white text-sm"
              style={{ fontSize: '18px' }}
            >
              {iconInfo?.fallbackIcon || 'apps'}
            </span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="font-bold text-[15px] text-white leading-tight mb-0.5">{title}</div>
        <div
          className="text-[14px] text-gray-200 leading-snug break-words"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxHeight: '2.8em',
            lineHeight: '1.4em',
          }}
          title={message}
        >
          {message}
        </div>
      </div>
      <div className="text-[11px] text-gray-400 self-start pt-1 pl-2 font-medium">now</div>
    </div>
  );
};

// In-device notification container (renders inside the phone/tablet screen)
const NotificationContainer = () => {
  const notifications = useSelector((state) => state.notifications.notifications);
  const channelConfig = useSelector((state) => state.channel.channelConfig);
  const agentStatus = useSelector((state) => state.channel.agentStatus);
  const currentChannel = useSelector((state) => state.channel.currentChannel);
  const deviceType = useSelector((state) => state.device.type);
  const dispatch = useDispatch();

  // Get channel icon info based on notification channel or message content
  const getChannelIconInfo = (notification) => {
    const { message, channel } = notification;
    
    let channelKey = null;
    
    // First, check if notification has explicit channel
    if (channel) {
      // Map 'offer' channel to 'app' channel for icon display
      if (channel === 'offer') {
        channelKey = 'app';
      } else {
        channelKey = channel;
      }
    }
    
    // If no explicit channel, try to infer from message content
    if (!channelKey && message) {
      const lower = message.toLowerCase();
      if (lower.includes('sms') || lower.includes('message')) channelKey = 'sms';
      else if (lower.includes('email')) channelKey = 'email';
      else if (lower.includes('call')) channelKey = 'call';
      else if (lower.includes('calendar')) channelKey = 'calendar';
      else if (lower.includes('browser')) channelKey = 'browser';
      else if (lower.includes('agent')) channelKey = 'agent';
      else if (lower.includes('offer') || lower.includes('banking') || lower.includes('account')) channelKey = 'app';
    }
    
    if (!channelKey || !channelConfig[channelKey]) {
      // Default to app icon for unknown types
      channelKey = 'app';
    }
    
    const config = channelConfig[channelKey] || channelConfig['app'] || {};
    
    // Check if custom icon URL exists and use it
    if (config.customIconUrl) {
      return { 
        type: 'custom', 
        url: config.customIconUrl,
        fallbackIcon: config.icon || 'apps',
        fallbackColor: config.color || '#007AFF'
      };
    }
    
    // Otherwise use Material Icon with channel color
    return { 
      type: 'material', 
      icon: config.icon || 'apps',
      color: config.color || '#007AFF'
    };
  };

  return (
    <div 
      className="absolute top-14 left-0 right-0 w-auto flex flex-col items-center z-50 pointer-events-none"
      style={{ transform: 'translateZ(0)' }} // Force GPU acceleration and ensure proper positioning
    >
      <AnimatePresence>
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={{ opacity: 0, y: -60, scale: 0.95, x: 0 }}
            animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
            exit={{ opacity: 0, y: -60, scale: 0.95, transition: { duration: 0.25 } }}
            transition={{ 
              type: 'spring', 
              stiffness: 400, 
              damping: 30, 
              duration: 0.5,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            className="mb-3 pointer-events-auto w-full"
            onClick={() => {
              // Notification navigation: always allow, but stop agent if needed
              const nextChannel = notification.action?.channel || notification.channel;
              if (currentChannel === 'agent' && nextChannel !== 'agent' && (agentStatus === 'listening' || agentStatus === 'speaking')) {
                dispatch({ type: 'channel/requestAgentStopAndNavigate', payload: { nextChannel, viaNotification: true } });
              } else {
                if (notification.action) {
                  dispatch(setChannel(notification.action.channel));
                  if (notification.action.channel === 'browser') {
                    dispatch(setBrowserUrl(notification.action.url));
                  } else if (notification.action.channel === 'app') {
                    dispatch(setAppChannelUrl({ url: notification.action.url }));
                  }
                } else if (notification.channel) {
                  dispatch(setChannel(notification.channel));
                }
              }
              dispatch(dismissNotification(notification.id));
            }}
          >
            <Notification
              iconInfo={getChannelIconInfo(notification)}
              title={notification.title || ''}
              message={notification.message}
              isTablet={deviceType === 'tablet'}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default NotificationContainer;

